import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  VotingResults,
  AdditionalResults,
} from "../../../../server/results";
import { api } from "../../api";
import Selector from "../../DesignSystem/Selector";
import { EntryType } from "../CompoPage";
import { Entry } from "../../models/entries";
import { Compo } from "../../models/compos";
import { Util } from "../../util";
import { RLink } from "../../DesignSystem/RLink";
import { CompoCategoriesClient } from "../../../../src/entity/CompoCategoriesClient";
import { User, type UserJSON } from "../../models/users";
import { PlayList } from "../../Toolbar/PlayList";
import { ResultColumns } from "./ResultColumns";
import { Button } from "../../DesignSystem/Button";

type VotingResultsState = VotingResults & {
  categories: VotingResults["categories"];
  compo: Compo;
  category_names: CompoCategoriesClient[];
};

type AdditionalResultsState = AdditionalResults & {
  nicest: { average: number; user: User }[];
  meannest: { average: number; user: User }[];
  averageVote: { average: number; user: User }[];
};

const buildCategoryNames = (categories: CompoCategoriesClient[]) =>
  Util.SortByKey(categories, (key) => {
    if (key.type === "song") return 1;
    if (key.type === "art") return 2;
    if (key.type === "story") return 3;
    return 0;
  });

export const ResultsPage = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [votingResults, setVotingResults] = useState<VotingResultsState>();
  const [additionalResults, setAdditionalResults] =
    useState<AdditionalResultsState>();

  useEffect(() => {
    let cancelled = false;

    const fetchResults = async () => {
      const results = await api.get<"/api/compo/:id/results">(
        `/api/compo/${id}/results`
      );

      if (api.isToast(results) || cancelled) {
        return;
      }

      const { nicest, meannest, averageVote, matchmaking, flattened } =
        await api.get<"/api/compo/:id/additional-results">(
          `/api/compo/${id}/additional-results`
        );

      if (cancelled) {
        return;
      }

      const sortedCategories = buildCategoryNames(
        results.category_names.map(
          (category) => new CompoCategoriesClient(category)
        )
      ).filter((category) => {
        return (
          results.categories.filter(
            (cat) =>
              cat[0] && cat[0].entry && cat[0].entry.type === category.type
          ).length > 0
        );
      });

      setVotingResults({
        categories: results.categories.map((category) =>
          category.map((row) => ({
            ...row,
            entry: new Entry(row.entry),
          }))
        ),
        compo: new Compo(results.compo),
        category_names: sortedCategories,
        top_reviewers: results.top_reviewers,
        userToVoteAccuracy: results.userToVoteAccuracy,
        best_guessers: results.best_guessers,
        obvious_songs: results.obvious_songs,
        entry_users: results.entry_users,
      });

      setAdditionalResults({
        nicest: nicest.map((entry) => ({
          average: entry.average,
          user: new User(entry.user),
        })),
        meannest: meannest.map((entry) => ({
          average: entry.average,
          user: new User(entry.user),
        })),
        averageVote: averageVote.map((entry) => ({
          average: entry.average,
          user: new User(entry.user),
        })),
        matchmaking,
        flattened,
      });
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const mostAccurateVoters = useMemo(() => {
    if (!votingResults) {
      return [];
    }

    const voters: { user_id: number; accuracy: number; user: UserJSON }[] = [];
    for (const userId in votingResults.userToVoteAccuracy) {
      const accuracyInfo = votingResults.userToVoteAccuracy[userId];
      if (accuracyInfo.votes > votingResults.categories[0].length - 10) {
        voters.push({
          user_id: Number(userId),
          accuracy: Number(accuracyInfo.acc),
          user: accuracyInfo.user,
        });
      }
    }

    return voters.sort((a, b) => a.accuracy - b.accuracy).slice(0, 5);
  }, [votingResults]);

  if (!votingResults || !additionalResults) {
    return (
      <div className="bg-slate-100 dark:bg-slate-900 px-5 py-8 text-white sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
        <div className="flex items-center gap-2 text-slate-400">
          <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
          Loading results...
        </div>
      </div>
    );
  }

  const compo = votingResults.compo;

  return (
    <div className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* Header Section */}
      <div className="px-5 py-8 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
        <h1 className="text-3xl font-bold">
          <span className="text-chips-gold-500 dark:text-chips-gold">
            {compo.name}
          </span>{" "}
          results.
        </h1>

        <p className="mt-2 text-xl text-slate-600 dark:text-slate-300">
          {compo.theme_short}
        </p>

        <div className="mt-4 flex items-center gap-3">
          <PlayList
            entries={votingResults.categories[0].map(
              (category) => category.entry
            )}
            desc="Play winners!"
          />
          <span className="text-slate-600">|</span>
          <RLink
            to={compo.linkToCompo}
            className="text-emerald-400 hover:text-emerald-300"
          >
            See the theme page.
          </RLink>
        </div>
      </div>

      {/* Results Grid */}
      <div className="px-5 py-6 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
        <Selector
          prefix="Showing results for: "
          choices={votingResults.category_names.map(
            (category) => category.type
          )}
          suffix="entries."
        >
          {votingResults.category_names.map((names) => (
            <ResultColumns
              key={names.type}
              categoryNames={names.list}
              type={names.type as EntryType}
              categories={votingResults.categories.map((category) =>
                category.filter((row) => row.entry.type === names.type)
              )}
              entryUsers={votingResults.entry_users}
            />
          ))}
        </Selector>

        {/* Special Awards */}
        <h2 className="mb-4 mt-8 text-2xl font-semibold">Special Awards!</h2>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Best Guessers */}
          <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
            <div className="flex flex-row content-between justify-between w-full">
              <h3 className="mb-3 text-lg font-semibold">Best Guessers</h3>
              <Button
                to={`/compo/${id}/guesstable`}
                variant="text"
                grow={false}
              >
                See all &rarr;
              </Button>
            </div>
            <div className="space-y-2">
              {votingResults.best_guessers.map((guesser) => (
                <div
                  key={guesser.username}
                  className="text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {guesser.username}
                  </span>{" "}
                  with {guesser.usernames.length} correct guesses
                </div>
              ))}
            </div>
          </div>

          {/* Most Obvious Songs */}
          <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
            <h3 className="mb-3 text-lg font-semibold">Most Obvious Songs</h3>
            <div className="space-y-2">
              {votingResults.obvious_songs.map((obviousSong) => (
                <div
                  key={obviousSong.entry_id}
                  className="text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {obviousSong.title}
                  </span>{" "}
                  with {obviousSong.users.length} correct guesses
                </div>
              ))}
            </div>
          </div>

          {/* Top Reviewers */}
          {votingResults.top_reviewers[0] &&
            votingResults.top_reviewers[0].favorites_count > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
                <h3 className="mb-3 text-lg font-semibold">Top Reviewers</h3>
                <div className="space-y-2">
                  {votingResults.top_reviewers
                    .filter((reviewer) => reviewer.favorites_count > 0)
                    .map(({ username, favorites_count }) => (
                      <div
                        key={username}
                        className="text-sm text-slate-600 dark:text-slate-300"
                      >
                        <RLink
                          to={`/user/${username}`}
                          className="font-medium text-slate-900 dark:text-white hover:text-emerald-400"
                        >
                          {username}
                        </RLink>{" "}
                        with {favorites_count} favorites
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* Nicest Voters */}
          <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
            <h3 className="mb-3 text-lg font-semibold">Nicest Voters</h3>
            <div className="space-y-2">
              {additionalResults.nicest.map(({ user: voter, average }) => (
                <div
                  key={voter.id}
                  className="text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {voter.username}
                  </span>{" "}
                  with avg <span className="text-emerald-400">{average}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Most Accurate Voters */}
          <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
            <h3 className="text-lg font-semibold">Most Accurate Voters</h3>
            <p className="mb-4 text-xs text-slate-500">
              scored by average difference
            </p>
            <div className="space-y-2">
              {mostAccurateVoters.map(({ accuracy, user: voter }) => (
                <div
                  key={voter.username}
                  className="text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {voter.username}
                  </span>{" "}
                  with diff{" "}
                  <span className="text-emerald-400">
                    {Math.floor(accuracy * 100) / 100}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* One True Pairing */}
          <div className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-800 p-4">
            <h3 className="mb-3 text-lg font-semibold">One True Pairing</h3>
            <div className="space-y-2">
              {additionalResults.matchmaking.map((pairing) => (
                <div
                  key={`${pairing.user1.id}-${pairing.user2.id}`}
                  className="text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="font-medium text-slate-900 dark:text-white">
                    {pairing.user1.username}
                  </span>
                  <i
                    className="fa fa-heart mx-1.5 text-pink-400"
                    aria-hidden="true"
                  ></i>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {pairing.user2.username}
                  </span>{" "}
                  <span className="text-emerald-400">
                    {pairing.diff.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
